
import { PageContainer } from "@/components/ui/page-container";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PeriodTracker } from "@/components/period/PeriodTracker";
import { PeriodTrackingForm } from "@/components/period-tracking/form/PeriodTrackingForm";
import CycleChart from "@/components/period-tracking/CycleChart";
import { HormoneInfo } from "@/components/period-tracking/HormoneInfo";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PhaseInfo } from "@/components/period-tracking/info/PhaseInfo";
import { NextPeriodInfo } from "@/components/period-tracking/info/NextPeriodInfo";
import { CycleStats } from "@/components/period-tracking/stats/CycleStats";
import { calculatePhase, calculateNextPeriod } from "@/components/period-tracking/utils/cycleCalculations";
import { Plus } from "lucide-react";

const PeriodTracking = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  const { data: periodData, refetch, isLoading } = useQuery({
    queryKey: ["period-tracking"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("period_tracking")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching period data:', error);
        throw error;
      }
      
      return data;
    },
  });

  const lastPeriodDate = periodData?.last_period_date ? new Date(periodData.last_period_date) : undefined;
  const periodDuration = periodData?.period_duration || "";
  const cycleLength = periodData?.cycle_length || "";

  const phase = lastPeriodDate && cycleLength && periodDuration 
    ? calculatePhase(lastPeriodDate, String(cycleLength), String(periodDuration))
    : null;
    
  const nextPeriod = lastPeriodDate && cycleLength 
    ? calculateNextPeriod(lastPeriodDate, String(cycleLength))
    : null;

  return (
    <PageContainer 
      title="Suivi de Cycle Menstruel"
      variant="pregnancy"
      intensity="medium"
      className="py-6"
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">
            Suivez votre cycle menstruel, prédisez vos prochaines règles et votre période de fertilité.
          </p>
          <Button 
            onClick={() => setIsFormOpen(true)}
            className="bg-pink-500 hover:bg-pink-600 text-white"
          >
            <Plus className="mr-2 h-4 w-4" /> Ajouter un cycle
          </Button>
        </div>

        {isFormOpen && (
          <PeriodTrackingForm 
            periodData={periodData}
            onSuccess={() => {
              refetch();
              setIsFormOpen(false);
            }}
            initialData={{
              lastPeriodDate,
              periodDuration: String(periodDuration),
              cycleLength: String(cycleLength)
            }}
            open={isFormOpen}
            onOpenChange={setIsFormOpen}
          />
        )}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="info">Informations</TabsTrigger>
            <TabsTrigger value="cycle">Cycle</TabsTrigger>
            <TabsTrigger value="hormones">Hormones</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            {lastPeriodDate && cycleLength && periodDuration ? (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  {phase && <PhaseInfo phase={phase} />}
                  {nextPeriod && <NextPeriodInfo nextPeriod={nextPeriod} />}
                </div>
                <CycleStats 
                  lastPeriodDate={lastPeriodDate} 
                  cycleLength={String(cycleLength)} 
                  periodDuration={String(periodDuration)} 
                />
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  Veuillez entrer les informations de votre cycle pour voir les détails
                </p>
                <Button 
                  onClick={() => setIsFormOpen(true)}
                  className="bg-pink-500 hover:bg-pink-600 text-white"
                >
                  Entrer mes informations
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="info">
            <PeriodTracker />
          </TabsContent>

          <TabsContent value="cycle">
            {lastPeriodDate && cycleLength ? (
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-lg border border-primary/20">
                <h3 className="font-semibold mb-4">Cycle Menstruel</h3>
                <div className="w-full max-w-2xl mx-auto">
                  <CycleChart 
                    lastPeriodDate={lastPeriodDate}
                    cycleLength={String(cycleLength)}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  Veuillez entrer les informations de votre cycle pour voir le graphique
                </p>
                <Button 
                  onClick={() => setIsFormOpen(true)}
                  className="bg-pink-500 hover:bg-pink-600 text-white"
                >
                  Entrer mes informations
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="hormones">
            <HormoneInfo />
          </TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
};

export default PeriodTracking;
